import { useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { Feld, Fehler, InfoTip } from '../../components/ui';
import { useEntityActions } from '../../hooks';
import type { StepProps } from '../CasePage';

const SOURCES = ['VERTRAG', 'BAUBESCHRIEB', 'PLAN', 'MUSTER', 'ZUGESICHERTE_EIGENSCHAFT', 'NORMVERWEIS'];

/** Bewertungskontext (D-053) — Struktur- und Vergleichsgrössen, nie ein Rechenwert. */
const EXPOSITIONSGRADE = ['VERDECKT', 'NEBENBEREICH', 'SICHTBEREICH', 'REPRAESENTATIV'];
const ERWARTUNGSKLASSEN = ['EINFACH', 'MITTEL', 'GEHOBEN', 'LUXUS'];

/**
 * Zeigt eine Kontexteinstufung an. Eine Einstufung ohne Beleg ist eine blosse Annahme
 * und darf keinen Abschlag stützen — das wird hier sichtbar gemacht, nicht verhindert.
 */
function KontextZeile({
  gruppe,
  wert,
  beleg,
}: {
  gruppe: 'exposition' | 'erwartung';
  wert?: string | null;
  beleg?: string | null;
}) {
  if (!wert) return null;
  return (
    <p className="meta">
      {t(`kontext.${gruppe}.label`)}: {t(`kontext.${gruppe}.${wert}`)}
      {!beleg && <> · {t('kontext.warnUnbelegt')}</>}
    </p>
  );
}

/** Schritt 3: Sollzustand — Anforderungen mit Quelle (Auftrag 7.3). */
export function Step3Soll({ caseId, data, reload, readonly }: StepProps) {
  return (
    <>
      <ObjekteKarte caseId={caseId} data={data} reload={reload} readonly={readonly} />
      <BauteileKarte caseId={caseId} data={data} reload={reload} readonly={readonly} />
      <AnforderungenKarte caseId={caseId} data={data} reload={reload} readonly={readonly} />
    </>
  );
}

type SubProps = Pick<StepProps, 'caseId' | 'data' | 'reload' | 'readonly'>;

function ObjekteKarte({ caseId, data, reload, readonly }: SubProps) {
  const actions = useEntityActions(caseId, 'building-objects', reload);
  const leer = {
    name: '',
    address: '',
    objectType: '',
    constructionYear: '',
    erwartungsklasse: '',
    erwartungsklasseBeleg: '',
  };
  const [form, setForm] = useState(leer);

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await actions.create({
      name: form.name,
      address: form.address || undefined,
      objectType: form.objectType || undefined,
      constructionYear: form.constructionYear ? Number(form.constructionYear) : undefined,
      erwartungsklasse: form.erwartungsklasse || undefined,
      erwartungsklasseBeleg: form.erwartungsklasseBeleg || undefined,
    });
    if (ok) setForm(leer);
  }

  return (
    <section className="karte">
      <h2>{t('step3.objects')}</h2>
      {data.buildingObjects.length === 0 && <div className="leer-hinweis">{t('step3.objectsEmpty')}</div>}
      {data.buildingObjects.map((o: any) => (
        <div className="eintrag" key={o.id}>
          <div className="eintrag-kopf">
            <strong>{o.name}</strong>
            {!readonly && (
              <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => actions.remove(o.id)}>
                {t('common.delete')}
              </button>
            )}
          </div>
          <p className="meta">
            {[o.address, o.objectType, o.constructionYear ? `Baujahr ${o.constructionYear}` : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <KontextZeile gruppe="erwartung" wert={o.erwartungsklasse} beleg={o.erwartungsklasseBeleg} />
        </div>
      ))}
      {!readonly && (
        <form onSubmit={add}>
          <div className="feld-zeile">
            <Feld label={t('step3.objectName')} required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </Feld>
            <Feld label={t('step3.objectAddress')}>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld label={t('step3.objectType')}>
              <input value={form.objectType} onChange={(e) => setForm({ ...form, objectType: e.target.value })} />
            </Feld>
            <Feld label={t('step3.constructionYear')}>
              <input
                type="number"
                min={1400}
                max={2200}
                value={form.constructionYear}
                onChange={(e) => setForm({ ...form, constructionYear: e.target.value })}
              />
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld label={t('kontext.erwartung.label')} hint={t('kontext.erwartung.hint')}>
              <select
                value={form.erwartungsklasse}
                onChange={(e) => setForm({ ...form, erwartungsklasse: e.target.value })}
              >
                <option value="">{t('kontext.keine')}</option>
                {ERWARTUNGSKLASSEN.map((k) => (
                  <option key={k} value={k}>{t(`kontext.erwartung.${k}`)}</option>
                ))}
              </select>
            </Feld>
            <Feld label={t('kontext.erwartung.beleg')}>
              <input
                value={form.erwartungsklasseBeleg}
                onChange={(e) => setForm({ ...form, erwartungsklasseBeleg: e.target.value })}
              />
            </Feld>
          </div>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">{t('common.add')}</button>
          </div>
        </form>
      )}
    </section>
  );
}

function BauteileKarte({ caseId, data, reload, readonly }: SubProps) {
  const actions = useEntityActions(caseId, 'building-elements', reload);
  const leer = {
    name: '',
    ebkphCode: '',
    buildingObjectId: '',
    expositionsgrad: '',
    expositionsgradBeleg: '',
  };
  const [form, setForm] = useState(leer);

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await actions.create({
      name: form.name,
      ebkphCode: form.ebkphCode || undefined,
      buildingObjectId: form.buildingObjectId || undefined,
      expositionsgrad: form.expositionsgrad || undefined,
      expositionsgradBeleg: form.expositionsgradBeleg || undefined,
    });
    if (ok) setForm(leer);
  }

  return (
    <section className="karte">
      <h2>{t('step3.elements')}</h2>
      {data.buildingElements.length === 0 && <div className="leer-hinweis">{t('step3.elementsEmpty')}</div>}
      {data.buildingElements.map((el: any) => (
        <div className="eintrag" key={el.id}>
          <div className="eintrag-kopf">
            <strong>{el.name}</strong>
            {el.ebkphCode && <span className="badge">{el.ebkphCode}</span>}
            {!readonly && (
              <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => actions.remove(el.id)}>
                {t('common.delete')}
              </button>
            )}
          </div>
          <KontextZeile gruppe="exposition" wert={el.expositionsgrad} beleg={el.expositionsgradBeleg} />
        </div>
      ))}
      {!readonly && (
        <form onSubmit={add}>
          <div className="feld-zeile">
            <Feld label={t('step3.elementName')} required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </Feld>
            <Feld label={t('step3.ebkph')} hint={t('step3.ebkphHint')}>
              <input value={form.ebkphCode} onChange={(e) => setForm({ ...form, ebkphCode: e.target.value })} />
            </Feld>
            <Feld label={t('step3.objectRef')}>
              <select
                value={form.buildingObjectId}
                onChange={(e) => setForm({ ...form, buildingObjectId: e.target.value })}
              >
                <option value="">—</option>
                {data.buildingObjects.map((o: any) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld label={t('kontext.exposition.label')} hint={t('kontext.exposition.hint')}>
              <select
                value={form.expositionsgrad}
                onChange={(e) => setForm({ ...form, expositionsgrad: e.target.value })}
              >
                <option value="">{t('kontext.keine')}</option>
                {EXPOSITIONSGRADE.map((k) => (
                  <option key={k} value={k}>{t(`kontext.exposition.${k}`)}</option>
                ))}
              </select>
            </Feld>
            <Feld label={t('kontext.exposition.beleg')}>
              <input
                value={form.expositionsgradBeleg}
                onChange={(e) => setForm({ ...form, expositionsgradBeleg: e.target.value })}
              />
            </Feld>
          </div>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">{t('common.add')}</button>
          </div>
        </form>
      )}
    </section>
  );
}

function AnforderungenKarte({ caseId, data, reload, readonly }: SubProps) {
  const actions = useEntityActions(caseId, 'target-requirements', reload);
  const [form, setForm] = useState({
    description: '',
    source: 'VERTRAG',
    sourceDetail: '',
    normNumber: '',
    normEdition: '',
    buildingElementId: '',
    contractRelationId: '',
  });

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await actions.create({
      description: form.description,
      source: form.source,
      sourceDetail: form.sourceDetail || undefined,
      normNumber: form.normNumber || undefined,
      normEdition: form.normEdition || undefined,
      buildingElementId: form.buildingElementId || undefined,
      contractRelationId: form.contractRelationId || undefined,
    });
    if (ok)
      setForm({
        description: '',
        source: 'VERTRAG',
        sourceDetail: '',
        normNumber: '',
        normEdition: '',
        buildingElementId: '',
        contractRelationId: '',
      });
  }

  return (
    <section className="karte">
      <h2>
        {t('step3.requirements')}
        <InfoTip termKey="mangel" />
      </h2>
      <p className="erklaerung">{t('step3.requirementsIntro')}</p>
      {data.targetRequirements.length === 0 && (
        <div className="leer-hinweis">{t('step3.requirementsEmpty')}</div>
      )}
      {data.targetRequirements.map((r: any) => (
        <div className="eintrag" key={r.id}>
          <div className="eintrag-kopf">
            <strong>{r.description.slice(0, 120)}</strong>
            <span className="badge">{t(`target.source.${r.source}`)}</span>
            {!readonly && (
              <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => actions.remove(r.id)}>
                {t('common.delete')}
              </button>
            )}
          </div>
          <p className="meta">
            {[r.sourceDetail, r.normNumber ? `${r.normNumber} (${r.normEdition ?? '—'})` : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      ))}
      {!readonly && (
        <form onSubmit={add}>
          <Feld label={t('step3.requirementText')} required error={actions.fieldErrors['description']}>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
            />
          </Feld>
          <div className="feld-zeile">
            <Feld label={t('step3.source')} required>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>{t(`target.source.${s}`)}</option>
                ))}
              </select>
            </Feld>
            <Feld label={t('step3.sourceDetail')} hint={t('step3.sourceDetailHint')}>
              <input
                value={form.sourceDetail}
                onChange={(e) => setForm({ ...form, sourceDetail: e.target.value })}
              />
            </Feld>
          </div>
          {form.source === 'NORMVERWEIS' && (
            <div className="feld-zeile">
              <Feld label={t('step3.normNumber')} required hint={t('step3.normHint')}>
                <input
                  value={form.normNumber}
                  onChange={(e) => setForm({ ...form, normNumber: e.target.value })}
                  required
                />
              </Feld>
              <Feld label={t('step3.normEdition')} required>
                <input
                  value={form.normEdition}
                  onChange={(e) => setForm({ ...form, normEdition: e.target.value })}
                  required
                />
              </Feld>
            </div>
          )}
          <div className="feld-zeile">
            <Feld label={t('step3.elementRef')}>
              <select
                value={form.buildingElementId}
                onChange={(e) => setForm({ ...form, buildingElementId: e.target.value })}
              >
                <option value="">—</option>
                {data.buildingElements.map((el: any) => (
                  <option key={el.id} value={el.id}>{el.name}</option>
                ))}
              </select>
            </Feld>
            <Feld label={t('step3.relationRef')}>
              <select
                value={form.contractRelationId}
                onChange={(e) => setForm({ ...form, contractRelationId: e.target.value })}
              >
                <option value="">—</option>
                {data.contractRelations.map((rel: any) => (
                  <option key={rel.id} value={rel.id}>{rel.label}</option>
                ))}
              </select>
            </Feld>
          </div>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">{t('common.add')}</button>
          </div>
        </form>
      )}
    </section>
  );
}
